/**
 * <phototron-viewer> Web Component
 * 
 * Displays 3D models using Google Model-Viewer.
 * Supports AR, camera controls, auto-rotation, and more.
 * 
 * @example
 * ```html
 * <phototron-viewer
 *   asset-id="shoe-001"
 *   api-key="pk_live_xxxxx"
 *   width="600"
 *   height="400"
 *   auto-rotate="true"
 *   ar-enabled="true">
 * </phototron-viewer>
 * ```
 * 
 * @example Direct model URL (without API)
 * ```html
 * <phototron-viewer
 *   src="https://example.com/model.glb"
 *   poster="https://example.com/poster.webp"
 *   width="600"
 *   height="400"
 *   auto-rotate="true">
 * </phototron-viewer>
 * ```
 */

import { ApiClient } from '../core/api-client';

const TEMPLATE = document.createElement('template');
TEMPLATE.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
      overflow: hidden;
    }
    
    .container {
      width: 100%;
      height: 100%;
      position: relative;
      background: var(--phototron-bg, #050505);
    }
    
    model-viewer {
      width: 100%;
      height: 100%;
      --poster-color: transparent;
    }
    
    .loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: var(--phototron-text, #fff);
      font-family: system-ui, sans-serif;
      font-size: 14px;
      gap: 12px;
      transition: opacity 0.3s ease;
      z-index: 10;
    }
    
    .loading.hidden {
      opacity: 0;
      pointer-events: none;
    }
    
    .loading-spinner {
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.2);
      border-top-color: var(--phototron-accent, #00e5ff);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    .progress-bar {
      width: 120px;
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
      overflow: hidden;
    }
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, var(--phototron-accent, #00e5ff), #F97316);
      border-radius: 2px;
      width: 0%;
      transition: width 0.2s ease;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .error {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--phototron-bg, #050505);
      color: #ff6b6b;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      padding: 20px;
      text-align: center;
      box-sizing: border-box;
      z-index: 10;
    }
    
    .error.hidden {
      display: none;
    }
    
    .ar-button {
      position: absolute;
      bottom: 16px;
      right: 16px;
      background: var(--phototron-accent, #00e5ff);
      color: #000;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      display: none;
      z-index: 5;
    }
    
    .ar-button:hover {
      background: #00c4d9;
    }
    
    :host([ar-enabled]) .ar-button {
      display: block;
    }
    
    .badge {
      position: absolute;
      top: 12px;
      left: 12px;
      background: rgba(0, 0, 0, 0.6);
      color: #fff;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-family: system-ui, sans-serif;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      z-index: 5;
    }
    
    .badge.hidden {
      display: none;
    }
  </style>
  
  <div class="container">
    <div class="loading">
      <div class="loading-spinner"></div>
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
      <span class="loading-text">Loading 3D Model...</span>
    </div>
    <div class="error hidden"></div>
    <div class="badge hidden">3D</div>
  </div>
`;

export class PhototronViewer extends HTMLElement {
  private modelViewer: HTMLElement | null = null;
  private loadingEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private progressFill: HTMLElement | null = null;
  private badgeEl: HTMLElement | null = null;
  private containerEl: HTMLElement | null = null;
  private _isComponentConnected = false;
  private modelViewerLoaded = false;

  static get observedAttributes(): string[] {
    return [
      'asset-id',
      'api-key',
      'api-url',
      'src',
      'poster',
      'width',
      'height',
      'auto-rotate',
      'camera-controls',
      'ar-enabled',
      'exposure',
      'shadow-intensity',
      'environment-image',
      'tone-mapping',
      'field-of-view',
      'camera-orbit',
      'camera-target',
      'show-badge',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  async connectedCallback(): Promise<void> {
    this._isComponentConnected = true;
    this.shadowRoot!.appendChild(TEMPLATE.content.cloneNode(true));

    this.containerEl = this.shadowRoot!.querySelector('.container');
    this.loadingEl = this.shadowRoot!.querySelector('.loading');
    this.errorEl = this.shadowRoot!.querySelector('.error');
    this.progressFill = this.shadowRoot!.querySelector('.progress-fill');
    this.badgeEl = this.shadowRoot!.querySelector('.badge');

    // Set dimensions
    this.updateDimensions();

    // Ensure model-viewer is loaded
    await this.ensureModelViewerLoaded();

    // Initialize
    this.initialize();
  }

  disconnectedCallback(): void {
    this._isComponentConnected = false;
    this.cleanup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    if (name === 'width' || name === 'height') {
      this.updateDimensions();
    }

    // Reinitialize if key attributes change
    if (['asset-id', 'api-key', 'src'].includes(name)) {
      if (this._isComponentConnected && this.modelViewerLoaded) {
        this.initialize();
      }
    }

    // Update model-viewer attributes dynamically
    if (this.modelViewer) {
      this.updateModelViewerAttribute(name, newValue);
    }
  }

  private async ensureModelViewerLoaded(): Promise<void> {
    // Check if model-viewer is already defined
    if (customElements.get('model-viewer')) {
      this.modelViewerLoaded = true;
      return;
    }

    // Try to load from CDN
    try {
      await import('https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js');
      this.modelViewerLoaded = true;
    } catch {
      // If import fails, try script tag
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';
        script.onload = () => {
          this.modelViewerLoaded = true;
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load model-viewer'));
        document.head.appendChild(script);
      });
    }
  }

  private updateDimensions(): void {
    const width = this.getAttribute('width');
    const height = this.getAttribute('height');

    if (width) {
      this.style.width = width.includes('%') || width.includes('px') ? width : `${width}px`;
    }
    if (height) {
      this.style.height = height.includes('%') || height.includes('px') ? height : `${height}px`;
    }
  }

  private updateModelViewerAttribute(name: string, value: string | null): void {
    if (!this.modelViewer) return;

    const attrMap: Record<string, string> = {
      'auto-rotate': 'auto-rotate',
      'camera-controls': 'camera-controls',
      'ar-enabled': 'ar',
      'exposure': 'exposure',
      'shadow-intensity': 'shadow-intensity',
      'environment-image': 'environment-image',
      'tone-mapping': 'tone-mapping',
      'field-of-view': 'field-of-view',
      'camera-orbit': 'camera-orbit',
      'camera-target': 'camera-target',
    };

    const mvAttr = attrMap[name];
    if (!mvAttr) return;

    if (value === 'true' || value === '') {
      this.modelViewer.setAttribute(mvAttr, '');
    } else if (value === 'false' || value === null) {
      this.modelViewer.removeAttribute(mvAttr);
    } else {
      this.modelViewer.setAttribute(mvAttr, value);
    }
  }

  private async initialize(): Promise<void> {
    this.cleanup();
    this.showLoading();

    try {
      const { src, poster } = await this.loadModelData();
      if (!this._isComponentConnected) return;

      this.createModelViewer(src, poster);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Failed to load 3D model');
    }
  }

  private async loadModelData(): Promise<{ src: string; poster?: string }> {
    // Check for direct URL attributes first
    const src = this.getAttribute('src');
    if (src) {
      return {
        src,
        poster: this.getAttribute('poster') || undefined,
      };
    }

    // Otherwise, use API
    const assetId = this.getAttribute('asset-id');
    const apiKey = this.getAttribute('api-key');

    if (!assetId || !apiKey) {
      throw new Error('Either provide src directly or asset-id with api-key');
    }

    const apiUrl = this.getAttribute('api-url') || undefined;
    const client = new ApiClient({ apiKey, baseUrl: apiUrl });
    const model = await client.getModel(assetId);

    if (!model?.glb) {
      throw new Error('No 3D model available for this asset');
    }

    return {
      src: model.glb,
    };
  }

  private createModelViewer(src: string, poster?: string): void {
    if (!this.containerEl) return;

    // Create model-viewer element
    this.modelViewer = document.createElement('model-viewer');
    this.modelViewer.setAttribute('src', src);

    if (poster) {
      this.modelViewer.setAttribute('poster', poster);
    }

    // Apply attributes
    const autoRotate = this.getAttribute('auto-rotate');
    if (autoRotate !== 'false') {
      this.modelViewer.setAttribute('auto-rotate', '');
    }

    const cameraControls = this.getAttribute('camera-controls');
    if (cameraControls !== 'false') {
      this.modelViewer.setAttribute('camera-controls', '');
    }

    const arEnabled = this.getAttribute('ar-enabled');
    if (arEnabled === 'true') {
      this.modelViewer.setAttribute('ar', '');
      this.modelViewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
    }

    // Neutral, product-friendly defaults — model-viewer's legacy environment +
    // tone map can look harsh. Any attribute the host explicitly set wins.
    this.modelViewer.setAttribute('tone-mapping', this.getAttribute('tone-mapping') || 'neutral');
    this.modelViewer.setAttribute('environment-image', this.getAttribute('environment-image') || 'neutral');
    this.modelViewer.setAttribute('exposure', this.getAttribute('exposure') || '1');
    this.modelViewer.setAttribute('shadow-intensity', this.getAttribute('shadow-intensity') || '0.6');

    // Field of view from the capture optics + the material's crop. This can be
    // very tight (telephoto lens × tight crop, e.g. ~9°), so relax model-viewer's
    // default 25° min-FOV clamp — otherwise the model renders too small.
    const fov = this.getAttribute('field-of-view');
    if (fov) {
      this.modelViewer.setAttribute('min-field-of-view', '1deg');
      this.modelViewer.setAttribute('field-of-view', fov);
    }

    // Initial camera orbit — frames the model to match the 2D capture viewpoint
    // (azimuth/polar) and distance (radius, in the GLB's metres). Relax the
    // orbit-radius clamps so an explicit metre radius isn't auto-clamped — but
    // NOT to 1000 m: model-viewer derives the near plane as ~far/1000 and far
    // from the max-orbit radius, so a 1000 m cap pushes the near plane out to
    // ~1 m and silently CLIPS any tight (close) framing to nothing ("not
    // visible until you zoom out"). 10 m keeps the near plane at ~1 cm, which
    // is well inside any scanned object's framing distance.
    const cameraOrbit = this.getAttribute('camera-orbit');
    if (cameraOrbit) {
      this.modelViewer.setAttribute('min-camera-orbit', 'auto auto 0.01m');
      this.modelViewer.setAttribute('max-camera-orbit', 'auto auto 10m');
      this.modelViewer.setAttribute('camera-orbit', cameraOrbit);
    }

    // Explicit orbit target = the object centre (camera-ray convergence), so
    // framing ignores the GLB bounding box — a floating mesh scrap can't offset
    // the centre the way model-viewer's default bbox-centre target would.
    const cameraTarget = this.getAttribute('camera-target');
    if (cameraTarget) {
      this.modelViewer.setAttribute('camera-target', cameraTarget);
    }

    // Event listeners
    this.modelViewer.addEventListener('load', () => {
      this.hideLoading();
      this.showBadge();
      this.dispatchEvent(new CustomEvent('load'));
    });

    this.modelViewer.addEventListener('error', (event) => {
      this.showError('Failed to load 3D model');
      this.dispatchEvent(new CustomEvent('error', { detail: event }));
    });

    this.modelViewer.addEventListener('progress', (event: Event) => {
      const progress = (event as CustomEvent).detail?.totalProgress || 0;
      this.updateProgress(progress * 100);
    });

    this.containerEl.appendChild(this.modelViewer);
  }

  private showLoading(): void {
    this.loadingEl?.classList.remove('hidden');
    this.errorEl?.classList.add('hidden');
    this.badgeEl?.classList.add('hidden');
  }

  private hideLoading(): void {
    this.loadingEl?.classList.add('hidden');
  }

  private showError(message: string): void {
    this.loadingEl?.classList.add('hidden');
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }
  }

  private updateProgress(percent: number): void {
    if (this.progressFill) {
      this.progressFill.style.width = `${percent}%`;
    }
  }

  private showBadge(): void {
    const showBadge = this.getAttribute('show-badge');
    if (showBadge !== 'false' && this.badgeEl) {
      this.badgeEl.classList.remove('hidden');
    }
  }

  private cleanup(): void {
    if (this.modelViewer) {
      this.modelViewer.remove();
      this.modelViewer = null;
    }
  }

  // Public API
  public getModelViewer(): HTMLElement | null {
    return this.modelViewer;
  }

  public refresh(): void {
    this.initialize();
  }
}

// Register custom element
if (typeof customElements !== 'undefined' && !customElements.get('phototron-viewer')) {
  customElements.define('phototron-viewer', PhototronViewer);
}
